import { useState, useEffect, useRef } from 'react';
import { NearbyStore } from '../../services/storeMapService';
import { normalizeChannel } from '../../utils/channelNormalizer';
import { GoogleMapViewHandle } from '../../components/GoogleMapView';
import StoreListItem from './StoreListItem';
import './StoreSidebar.css';

interface StoreSidebarProps {
  stores: NearbyStore[];
  referenceStore?: NearbyStore;
  selectedStoreId?: string | null;
  onSelectStore?: (storeId: string) => void;
  onOpenInfoWindow?: (storeId: string) => void;
  mapViewRef?: React.RefObject<GoogleMapViewHandle | null>;
  radiusFilter?: number;
  storeTypeFilter?: string;
  storesHaveFilter?: string[];
  storesDontHaveFilter?: string[];
  showOptionFilter?: string;
  channelFilter?: string[];
}

function StoreSidebar({ stores, referenceStore, selectedStoreId, onSelectStore, onOpenInfoWindow, mapViewRef, radiusFilter = 5, storeTypeFilter = 'All Stores', storesHaveFilter = [], storesDontHaveFilter = [], showOptionFilter = 'All Stores in Neighbourhood', channelFilter = [] }: StoreSidebarProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const storeListRef = useRef<HTMLDivElement>(null);
  const storeCardRefsRef = useRef<{ [key: string]: HTMLDivElement | null }>({});

  // Check if store has a solution based on solution code
  const hasSolution = (store: NearbyStore, solutionCode: string): boolean => {
    switch (solutionCode) {
      case 'P':
        return !!store.purchek_install_date;
      case 'CM':
        return !!store.cart_manager_install_date;
      case 'CC':
        return !!store.containment_install_date;
      case 'S':
        return !!store.subscription_start_date;
      case 'PW':
        return !!store.paired_wheel_install_date;
      default:
        return false;
    }
  };

  // Helper function to check if store has any solution installed
  const hasAnySolutionInstalled = (store: NearbyStore): boolean => {
    return !!(
      store.purchek_install_date ||
      store.containment_install_date ||
      store.cart_manager_install_date ||
      store.subscription_start_date ||
      store.paired_wheel_install_date
    );
  };

  const filteredStores = stores.filter((store) => {
    // Exclude the reference store from the nearby stores list (it's shown at the top)
    if (referenceStore && store.id && referenceStore.id && store.id === referenceStore.id) {
      return false;
    }

    const searchTerm = searchQuery.toLowerCase();
    const withinRadius = (store.distance || 0) <= radiusFilter;

    // Check store type filter
    // Customer: has at least one solution installed OR has customer_id
    // Non-Customer: has NO solutions installed AND no customer_id
    const isCustomer = hasAnySolutionInstalled(store) || !!store.customer_id;
    if (storeTypeFilter === 'Customers' && !isCustomer) return false;
    if (storeTypeFilter === 'Non-Customer' && isCustomer) return false;

    // Check "Stores Have" filter - store must have ALL selected solutions
    if (storesHaveFilter.length > 0) {
      const hasAllRequired = storesHaveFilter.every((solution) => hasSolution(store, solution));
      if (!hasAllRequired) return false;
    }

    // Check "Stores Don't Have" filter - store must NOT have ANY of these solutions
    if (storesDontHaveFilter.length > 0) {
      const hasAny = storesDontHaveFilter.some((solution) => hasSolution(store, solution));
      if (hasAny) return false;
    }

    // Check "Neighbourhood Store Type" filter
    if (showOptionFilter === 'Located in Shopping Complex' && (!store.plaza_cm_customer_count || store.plaza_cm_customer_count <= 0)) {
      return false;
    }

    // Check channel filter
    if (channelFilter.length > 0 && !channelFilter.includes(normalizeChannel(store.channel || ''))) {
      return false;
    }

    return (
      withinRadius &&
      (
        (store.site?.toLowerCase().includes(searchTerm) || false) ||
        (store.address?.toLowerCase().includes(searchTerm) || false)
      )
    );
  });

  // Scroll to selected store card when selectedStoreId changes
  useEffect(() => {
    if (selectedStoreId && storeCardRefsRef.current[selectedStoreId] && storeListRef.current) {
      const cardElement = storeCardRefsRef.current[selectedStoreId];
      const listContainer = storeListRef.current;

      // Calculate scroll position to center the card in the list
      const cardTop = cardElement.offsetTop;
      const cardHeight = cardElement.offsetHeight;
      const containerHeight = listContainer.offsetHeight;
      const scrollPosition = cardTop - (containerHeight - cardHeight) / 2;

      // Scroll only the list container, not the entire page
      listContainer.scrollTo({
        top: scrollPosition,
        behavior: 'smooth'
      });
    }
  }, [selectedStoreId]);

  // Build filter description
  const getFilterDescription = () => {
    const filters: string[] = [];

    // Customer type filter
    if (storeTypeFilter === 'Customers') {
      filters.push('Customers');
    } else if (storeTypeFilter === 'Non-Customer') {
      filters.push('Non-Customers');
    }

    // Stores have filter
    if (storesHaveFilter.length > 0) {
      const solutionNames: { [key: string]: string } = {
        'P': 'Purchek',
        'CM': 'Cart Manager',
        'CC': 'Containment',
        'S': 'Subscription',
        'PW': 'Paired Wheel'
      };
      const names = storesHaveFilter.map((code) => solutionNames[code] || code).join(', ');
      filters.push(`with ${names}`);
    }

    // Stores don't have filter
    if (storesDontHaveFilter.length > 0) {
      const solutionNames: { [key: string]: string } = {
        'P': 'Purchek',
        'CM': 'Cart Manager',
        'CC': 'Containment',
        'S': 'Subscription',
        'PW': 'Paired Wheel'
      };
      const names = storesDontHaveFilter.map((code) => solutionNames[code] || code).join(', ');
      filters.push(`without ${names}`);
    }

    // Neighbourhood filter
    if (showOptionFilter === 'Located in Shopping Complex') {
      filters.push('in Shopping Complex');
    }

    if (channelFilter.length === 0) {
      filters.push('All Channels');
    } else if (channelFilter.length < 8) {
      const normalizedChannels = channelFilter.map(normalizeChannel);
      filters.push(`with Channel ${normalizedChannels.join(', ')}`);
    }

    return filters;
  };

  const filterTexts = getFilterDescription();

  return (
    <div className="store-sidebar">
      <div className="stores-sidebar-header">
        <span className="stores-count">{filteredStores.length} stores within {radiusFilter} miles</span>
        {filterTexts.length > 0 && (
          <p className="filter-description">
            Showing {filterTexts.join(', ')}
          </p>
        )}
      </div>

      {/* Search Bar */}
      <div className="search-wrapper">
        <input
          type="text"
          placeholder="Search by brand or store name..."
          className="search-input"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Stores List */}
      <div className="stores-list" ref={storeListRef}>
        {/* Reference Store Card */}
        {referenceStore && (
          <div ref={(el) => { if (referenceStore.id) storeCardRefsRef.current[referenceStore.id] = el; }}>
            <StoreListItem
              store={referenceStore}
              isReference={true}
              onSelect={() => {
                if (referenceStore.id) {
                  onSelectStore?.(referenceStore.id);
                  onOpenInfoWindow?.(referenceStore.id);
                }
              }}
            />
          </div>
        )}

        {/* Nearby Stores */}
        {filteredStores.length > 0 ? (
          filteredStores.map((store) => (
            <div
              key={store.id}
              ref={(el) => { if (store.id) storeCardRefsRef.current[store.id] = el; }}
            >
              <StoreListItem
                store={store}
                isReference={false}
                isSelected={selectedStoreId === store.id}
                onSelect={() => {
                  if (store.id) {
                    onSelectStore?.(store.id);
                    onOpenInfoWindow?.(store.id);
                  }
                }}
              />
            </div>
          ))
        ) : (
          <div className="no-results">
            {searchQuery ? 'No stores found matching your search' : 'No nearby stores'}
          </div>
        )}
      </div>
    </div>
  );
}

export default StoreSidebar;
